import produce from 'immer';
import React, { createContext, Dispatch, useContext, useReducer } from 'react';

import {
  ConnectionsContextActions,
  ConnectionsContextActionTypes,
  ConnectionsProps,
  ConnectionsProviderProps,
} from './ConnectionsContext.types';

const ConnectionsContext = createContext<ConnectionsProps | undefined>(undefined);
const ConnectionsDispatchContext = createContext<Dispatch<ConnectionsContextActions> | undefined>(
  undefined,
);

const initialConnectionsState: ConnectionsProps = {
  form: {
    companies: { value: true, indeterminate: false },
    branches: { value: true, indeterminate: false },
    persons: { value: true, indeterminate: false },
    phone: { value: true, indeterminate: false },
    email: { value: true, indeterminate: false },
    address: { value: true, indeterminate: false },
    website: { value: true, indeterminate: false },
    historicalType: { value: false, indeterminate: false },
    shareholder: { value: false, indeterminate: false },
    beneficialOwner: { value: false, indeterminate: false },
    administrator: { value: false, indeterminate: false },
    auditor: { value: false, indeterminate: false },
    memberOfTheBoard: { value: false, indeterminate: false },
    branchManager: { value: false, indeterminate: false },
    empoweredToSign: { value: false, indeterminate: false },
    founder: { value: false, indeterminate: false },
    liablePartner: { value: false, indeterminate: false },
    generalAgent: { value: false, indeterminate: false },
    liquidator: { value: false, indeterminate: false },
    management: { value: false, indeterminate: false },
    other: { value: false, indeterminate: false },
    memberOfSupervisoryBoard: { value: false, indeterminate: false },
  },

  formIndeterminate: {
    companies: false,
    branches: false,
    persons: false,
    phone: false,
    email: false,
    address: false,
    website: false,
    shareholder: false,
    beneficialOwner: false,
    administrator: false,
    auditor: false,
    memberOfTheBoard: false,
    branchManager: false,
    empoweredToSign: false,
    founder: false,
    liablePartner: false,
    generalAgent: false,
    liquidator: false,
    management: false,
    other: false,
    memberOfSupervisoryBoard: false,
  },
  companyId: '',
  path: [],
  connectionsCounts: { total: 0, displayed: 0 },
};

const ConnectionsReducer = (state: ConnectionsProps, action: ConnectionsContextActions) => {
  switch (action.type) {
    case ConnectionsContextActionTypes.SetFormState: {
      return produce(state, (draft) => {
        draft.form = action.payload;
      });
    }
    case ConnectionsContextActionTypes.SetCompanyId: {
      return produce(state, (draft) => {
        draft.companyId = action.payload;
      });
    }
    case ConnectionsContextActionTypes.NavigateToPath: {
      return produce(state, (draft) => {
        draft.path = draft.path.slice(0, action.payload + 1);
      });
    }
    case ConnectionsContextActionTypes.PushToPath: {
      return produce(state, (draft) => {
        const lastIdx = draft.path.length;
        if (!lastIdx || draft.path[lastIdx - 1].id !== action.payload.id) {
          draft.path.push(action.payload);
        }
      });
    }
    case ConnectionsContextActionTypes.SetFormIndeterminate: {
      return produce(state, (draft) => {
        draft.formIndeterminate = action.payload;
      });
    }
    case ConnectionsContextActionTypes.SetConnectionsCounts: {
      return produce(state, (draft) => {
        draft.connectionsCounts = action.payload;
      });
    }
  }
};

export const ConnectionsContextProvider = ({ children }: ConnectionsProviderProps) => {
  const [state, dispatch] = useReducer(ConnectionsReducer, initialConnectionsState);

  return (
    <ConnectionsContext.Provider value={state}>
      <ConnectionsDispatchContext.Provider value={dispatch}>
        {children}
      </ConnectionsDispatchContext.Provider>
    </ConnectionsContext.Provider>
  );
};

export const useConnectionsState = () => {
  const context = useContext(ConnectionsContext);

  if (context === undefined) {
    throw new Error('ConnectionsState must be used within a ConnectionsProvider');
  }

  return context;
};

export const useConnectionsDispatch = () => {
  const context = useContext(ConnectionsDispatchContext);

  if (context === undefined) {
    throw new Error('ConnectionsDispatch must be used within a ConnectionsProvider');
  }

  return context;
};
